import { Search } from 'lucide-react'
import { useDecoded4byte } from '../hooks/useDecoded4byte.js'
import type { DecodedCall } from '../lib/fourbyte.js'

interface Props {
  data: string | undefined
  /** Fold behind a disclosure - used when a verified ERC-7730 panel above
   *  already shows the same call, so this unverified copy is secondary. */
  collapsed?: boolean
}

/**
 * Async function-signature lookup against the OpenChain DB. Renders nothing
 * until resolved.
 */
export function DecodedCalldata({ data, collapsed = false }: Props) {
  const decoded = useDecoded4byte(data)
  if (!decoded) return null

  if (collapsed) {
    return (
      <details className="mt-2 text-xs">
        <summary className="cursor-pointer text-muted-foreground">4byte decode</summary>
        <DecodedBody decoded={decoded} />
      </details>
    )
  }
  return <DecodedBody decoded={decoded} />
}

function DecodedBody({ decoded }: { decoded: DecodedCall }) {
  return (
    <div className="mt-2 rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-sm">
      <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Search className="size-3" />
        4byte decode
      </div>
      <div className="font-mono font-semibold">
        {decoded.name}
        <span className="text-muted-foreground">(</span>
      </div>
      {decoded.args.map((arg, i) => (
        <div
          key={i}
          className="break-all pl-4 font-mono text-xs"
        >
          <span className="text-muted-foreground">{arg.type}</span>{' '}
          {arg.name && (
            <span className="text-muted-foreground">{arg.name} = </span>
          )}
          <span data-mask>{arg.value}</span>
          {i < decoded.args.length - 1 && (
            <span className="text-muted-foreground">,</span>
          )}
        </div>
      ))}
      <div className="font-mono text-muted-foreground">)</div>
      <div className="mt-1 text-[10px] text-muted-foreground">
        sig: <span className="font-mono">{decoded.signature}</span>
      </div>
    </div>
  )
}
